import { useMemo } from 'react'

import useQuery from '@hooks/useQuery'
import { GetEventsForRsvpFilterDocument } from '@frontend/graphql/queries/events.generated'
import type { EventsForRsvpFilterQueryType } from '@frontend/graphql/queries/events'
import { scopedTranslation } from '@utils/I18n'
import timeFormatter from '@utils/timeFormatter'
import type { SelectOption } from '@utils/types'

const t = scopedTranslation('components.filter_dialog')

type FilterableEvent = EventsForRsvpFilterQueryType['eventsForRsvp']['nodes'][number]

// The filter stores event ids; the select and the chips both show
// "<name> (<date>)", read from the same event list so an id resolves the same
// way everywhere, and fall back to "Event #<id>" while the list is loading or
// when the event has left it.
export function useEventRsvpOptions({ enabled = true }: { enabled?: boolean } = {}) {
  const response = useQuery<EventsForRsvpFilterQueryType>({
    key: ['eventsForRsvpFilter'],
    query: GetEventsForRsvpFilterDocument,
    enabled,
  })

  const events: FilterableEvent[] = response.data?.eventsForRsvp?.nodes ?? []

  const byId = useMemo(() => new Map(events.map((event) => [event.id, event])), [events])

  const optionLabel = (id: string): string => {
    const event = byId.get(id)
    if (!event) return t('event_rsvps.unknown_event', { id })

    return `${event.name} (${timeFormatter.fromISO(event.startTime, event.ianaTimeZone).toShortDate()})`
  }

  const options: SelectOption[] = events.map((event) => ({ value: event.id, label: optionLabel(event.id) }))

  return { events, options, optionLabel, isLoading: response.isLoading }
}
